import { useState, useRef } from 'react';
import { Button, Menu, MenuItem } from '@mui/material';
import dayjs from 'dayjs';
import {
  useGridApiContext,
  gridFilteredSortedRowIdsSelector,
  gridVisibleColumnFieldsSelector,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import SaveAltIcon from '@mui/icons-material/SaveAlt';

// Custom Toolbar for the DataGrid component within DisplayReport
// ------------------------------
// The only reason we have a custom toolbar is because we need to provide a custom CSV Export option
// which allows us to insert data which is not already displayed in the DataGrid
const CustomToolbar = ({ reportName, startDate, endDate, userName }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const apiRef = useGridApiContext();

  const formatDate = (date) => dayjs(date).format('DD/MM/YYYY');

  // converts an array of objects to a CSV array
  const jsonToCSV = (jsonArray) => {
    const array = typeof jsonArray !== 'object' ? JSON.parse(jsonArray) : jsonArray;
    if (!array.length) return '';
    const headers = Object.keys(array[0]);
    const csvRows = [
      ...array.map((row) => headers.map((field) => JSON.stringify(row[field] ?? '')).join(',')), // data rows
    ];
    return csvRows.join('\n');
  };

  // function to product CSV file from the DataGrid APIref
  const getCSV = (apiRef) => {
    const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);
    const visibleColumnsField = gridVisibleColumnFieldsSelector(apiRef);

    // Format the data from the DataGrid
    const data = filteredSortedRowIds.map((id) => {
      const row = {};
      visibleColumnsField.forEach((field) => {
        row[field] = apiRef.current.getCellParams(id, field).value;
      });
      return row;
    });

    // initialize new rows to add to data
    const headerRow = {};
    const titleRow = {};
    const criteriaRow = {};
    const footerRow = {};
    const blankRow = {};

    // initialize values for the new rows
    const titleRowValues = ['Title', reportName];
    const criteriaRowValues = [
      'Criteria',
      `Report Generated for Completed Date Range: ${formatDate(startDate)} to ${formatDate(endDate)}`,
    ];
    const footerRowValues = [`Report Run on ${formatDate()} by ${userName}`];

    // add the values to the new rows
    visibleColumnsField.forEach((field, i) => {
      headerRow[field] = field;
      titleRow[field] = titleRowValues[i] || '';
      criteriaRow[field] = criteriaRowValues[i] || '';
      footerRow[field] = footerRowValues[i] || '';
      blankRow[field] = '';
    });

    // structure our CSV file using the rows we have
    return jsonToCSV([titleRow, criteriaRow, blankRow, headerRow, ...data, blankRow, blankRow, footerRow]);
  };

  const exportBlob = (blob, filename) => {
    // Save the blob in a file
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    setTimeout(() => {
      URL.revokeObjectURL(url);
    });
  };

  const startExport = () => {
    const csvString = getCSV(apiRef);

    const BOM = '\uFEFF'; //utf-8 BOM
    const csvContent = BOM + csvString;

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8',
    });
    exportBlob(blob, `${reportName || 'Report'}.csv`);
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />

      <Button
        ref={triggerRef}
        id="export-menu-trigger"
        aria-controls="export-menu"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
        startIcon={<SaveAltIcon />}
      >
        Export
      </Button>
      <Menu
        id="export-menu"
        anchorEl={triggerRef.current}
        open={open}
        onClose={() => setOpen(false)}
        MenuListProps={{
          'aria-labelledby': 'export-menu-trigger',
        }}
      >
        <MenuItem onClick={startExport}>Download as CSV</MenuItem>
      </Menu>
    </GridToolbarContainer>
  );
};

export default CustomToolbar;
